import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { ReactComponent as EditIcon } from 'icons/edit.svg';
import { ReactComponent as SearchIcon } from 'icons/search.svg';
import { Button } from '@mui/material';
import HeaderLogo from 'components/common/HeaderLogo/HeaderLogo';
import ExternalClientAccessInfo from 'components/common/ExternalClientAccessInfo/ExternalClientAccessInfo';
import CopyNeedsAssessment from 'components/common/CopyNeedsAssessment/CopyNeedsAssessment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import actions from 'store/actions';
import { lastElement, sum } from 'utils/arrays';
import { getQuantity } from 'utils/categories';
import { getCountry } from 'utils/country';
import { setDateFormat } from 'utils/date';
import { downloadPdfReport } from 'utils/pdfReport';
import { addPlusMinus5PercentageRangeToNumber, addThousandSeparator } from 'utils/numbers';
import CategoryCard from './CategoryCard';
import HeadcountCharts from './HeadcountCharts';
import ShareTeamDrawer from './ShareTeamDrawer';
import styles from './step6.module.scss';
import { useTranslation } from 'react-i18next';
import { getFormatSeparatorsOptions } from 'config/marketConfig';
import searchParams from 'constants/searchParams';
import paths from 'constants/paths';
import { useStepsWizard } from 'hooks/useStepsWizard';
import { openFullPathInNewTab } from 'utils/paths';

const TOP_CATEGORIES_COUNT = 3;

const EditButton = ({ onEdit, isReadOnlyMode, step }) => {
  const { t } = useTranslation();
  return (
    <Button
      onClick={() => onEdit(step)}
      className={styles.assessment__card_edit_btn}
      size="small"
      variant="contained"
      startIcon={isReadOnlyMode ? <SearchIcon /> : <EditIcon />}>
      {isReadOnlyMode ? t('externalAccess.view') : t('step6.edit')}
    </Button>
  );
};

export default function Step6() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const isReport = location.pathname.includes('report');
  const params = new URLSearchParams(window.location.search);
  const preview = params.get(searchParams.preview);
  const isExternalAccess = useSelector((state) => state.general.isExternalAccessMode);
  const isReadOnlyMode = useSelector((state) => state.general.isReadOnlyMode);
  const externalAccessConfig = useSelector((state) => state.externalAccessConfig);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [pageScrollAboveThreshold, setPageScrollAboveThreshold] = useState(false);
  const [openSharedNeedsAssessmentsDrawer, setSharedNeedsAssessmentsDrawer] = useState(false);
  const na = useSelector((state) => state.na);
  const currentWorkforce = na.growth[0];
  const projectedWorkforce = na.growth[na.growth.length - 1];
  const naSpaceSelected = na.spaces.find((naSpace) => naSpace.id === na.workplacestyleSelected);
  const startingYear = new Date(na.startingYear).getFullYear();
  const leaseExpYear = new Date(na.leaseExpirationYear).getFullYear();
  const totalSeats = lastElement(na.growth.map((growth_row) => growth_row.seats));
  const topCategories = naSpaceSelected.spaceTypes
    .concat(naSpaceSelected.customSpaces)
    .map((spaceType) => {
      return {
        id: spaceType.id,
        name: spaceType.name,
        quantity: spaceType.spaces
          ? getQuantity(spaceType, getFormatSeparatorsOptions())
          : spaceType.quantity,
        value: spaceType.spaces
          ? sum(spaceType.spaces, (space) => space.quantity * space.size.width * space.size.height)
          : spaceType.quantity * spaceType.width * spaceType.height,
      };
    })
    .sort((a, b) => b.value - a.value)
    .slice(0, TOP_CATEGORIES_COUNT);
  const { goToStep } = useStepsWizard({
    naId: na.id,
    preview,
    isExternalAccess,
    accessKey: externalAccessConfig.accessKey,
  });

  const goToAssessment = (id) => {
    openFullPathInNewTab(`${paths.step}${paths.steps.yourPeople}/${id}`);
  };

  const handleScroll = () => {
    if (window.scrollY > 40) {
      setPageScrollAboveThreshold(true);
    } else {
      setPageScrollAboveThreshold(false);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    if (!isReadOnlyMode) dispatch(actions.warning.setShowWarning(true));
  }, [dispatch, isReadOnlyMode]);

  useEffect(
    () => () => {
      window.removeEventListener('scroll', handleScroll);
    },
    [],
  );

  const onCopyAssessmnent = (createdAssessment) => {
    if (createdAssessment) {
      goToAssessment(createdAssessment.id);
    }
    setShowCopyDialog(false);
  };

  const getPdfReport = async () => {
    await downloadPdfReport(
      {
        id: na.id,
        name: na.name,
        createdAt: na.createdAt,
        workplace: t(naSpaceSelected.name),
        terms: t('report.termsOfAgreement'),
      },
      isExternalAccess,
      externalAccessConfig.accessKey,
    );
  };

  const onEdit = (step) => {
    goToStep({ step });
  };

  return (
    <>
      {isReadOnlyMode && !isExternalAccess && <ExternalClientAccessInfo />}
      <div className={`${styles.wrapper} ${isReport && styles.report_wrapper}`}>
        <div
          data-testid="Step6Header"
          style={{ zIndex: pageScrollAboveThreshold ? 100 : 1 }}
          className={styles.header_container}>
          <HeaderLogo
            enableButtons={true}
            isReadOnlyMode={isReadOnlyMode}
            showCopyDialog={setShowCopyDialog}
            showDrawer={setSharedNeedsAssessmentsDrawer}
            getPdfReport={getPdfReport}
          />
        </div>
        <div className={styles.container}>
          <div className={styles.scorecard_bg}></div>
          <div className={styles.content_left}>
            <h2 className={styles.assessment__title}>{na.companyName}</h2>
            <div className={styles.assessment__basic_info_container}>
              <span className={styles.assessment__date}>
                {setDateFormat(na.createdAt, getCountry())}{' '}
              </span>
              -<span className={styles.assessment__space_style}> {t(naSpaceSelected.name)}</span>
            </div>
            <div className={styles.assessment__info_card}>
              <div className={styles.assessment__card_title_container}>
                <h3 className={styles.assessment__card_title}>{t('step6.yourSituation')}</h3>
                <EditButton
                  step={paths.steps.currentSituation}
                  isReadOnlyMode={isReadOnlyMode}
                  onEdit={onEdit}
                />
              </div>
              <div className={styles.assessment__card_content_container}>
                <div className={styles.assessment__card_content}>
                  <span className={styles.assessment__card_content_title}>
                    {t('step6.leaseExpDate')}
                  </span>
                  <span className={styles.assessment__card_content_value}>
                    {na.leaseExpirationYear ? leaseExpYear : '-'}
                  </span>
                </div>
                <div className={styles.divider}></div>
                <div className={styles.assessment__card_content}>
                  <span className={styles.assessment__card_content_title}>
                    {t('step6.startingYear')}
                  </span>
                  <span className={styles.assessment__card_content_value}>{startingYear}</span>
                </div>
                <div className={styles.assessment__card_content}>
                  <span className={styles.assessment__card_content_title}>
                    {t('step6.nextLeaseTerm')}
                  </span>
                  <span className={styles.assessment__card_content_value}>
                    {na.leaseTermInYears} {t('step6.years')}
                  </span>
                </div>
              </div>
            </div>
            <div className={styles.assessment__info_card}>
              <div className={styles.assessment__card_title_container}>
                <h3 className={styles.assessment__card_title}>{t('step6.forecastHeadcount')}</h3>
                <EditButton
                  step={paths.steps.yourPeople}
                  isReadOnlyMode={isReadOnlyMode}
                  onEdit={onEdit}
                />
              </div>
              <div className={styles.assessment__card_content_container}>
                <div className={styles.assessment__card_content}>
                  <span className={styles.assessment__card_content_title}>
                    {t('step6.currentHeadcount')}
                  </span>
                  <span className={styles.assessment__card_content_value}>
                    {addThousandSeparator(
                      currentWorkforce.totalHeadcount,
                      getFormatSeparatorsOptions(),
                    )}
                  </span>
                </div>
                <div className={styles.divider}></div>
                <div className={styles.assessment__card_content}>
                  <span className={styles.assessment__card_content_title}>
                    {t('step6.projectHeadcount')}
                  </span>
                  <span className={styles.assessment__card_content_value}>
                    {addThousandSeparator(
                      projectedWorkforce.totalHeadcount,
                      getFormatSeparatorsOptions(),
                    )}
                  </span>
                </div>
              </div>
              <HeadcountCharts
                currentWorkforce={currentWorkforce}
                projectedWorkforce={projectedWorkforce}
              />
            </div>
          </div>
          <div className={styles.content_right}>
            <div
              className={`${styles.assessment__office_info_card} ${styles.assessment__office_info_card_rsf}`}>
              <span className={styles.assessment__office_info_value}>
                {addPlusMinus5PercentageRangeToNumber(
                  naSpaceSelected.rentableSquareFeet,
                  getFormatSeparatorsOptions(),
                )}
              </span>
              <h3 className={styles.assessment__office_info_title}>{t('step6.rentableTotal')}</h3>
            </div>
            <div className={styles.assessment__info_container}>
              <div
                className={`${styles.assessment__office_info_card} ${styles.assessment__office_info_card_per_employee}`}>
                <span className={styles.assessment__office_info_value}>
                  {addThousandSeparator(
                    naSpaceSelected.rentableSquareFeetPerHead,
                    getFormatSeparatorsOptions(),
                  )}
                </span>
                <h3 className={styles.assessment__office_info_title}>
                  {t('step6.inOfficeAndHybridTotal')}
                </h3>
              </div>
              <div
                className={`${styles.assessment__office_info_card} ${styles.assessment__office_info_card_seats}`}>
                <span className={styles.assessment__office_info_value}>
                  {addThousandSeparator(totalSeats, getFormatSeparatorsOptions())}
                </span>
                <h3 className={styles.assessment__office_info_title}>
                  {t('step6.inOfficeAndHybridEmployees')}
                </h3>
              </div>
            </div>
            <div className={styles.categories_wrapper}>
              <div className={styles.assessment__card_title_container}>
                <h3 className={styles.assessment__card_title}>
                  {t('step6.topCategories', { count: TOP_CATEGORIES_COUNT })}
                </h3>
                <EditButton
                  step={paths.steps.customizeBuildOut}
                  isReadOnlyMode={isReadOnlyMode}
                  onEdit={onEdit}
                />
              </div>
              <div className={styles.categories_container}>
                {topCategories.map((item) => (
                  <CategoryCard
                    key={item.id}
                    value={addThousandSeparator(item.value, getFormatSeparatorsOptions())}
                    title={`${item.quantity} ${t(item.name)}`}
                  />
                ))}
                {!isReport && (
                  <div
                    onClick={() => onEdit(paths.steps.customizeBuildOut)}
                    className={styles.categories__link}>
                    {t('step6.seeAllCategories', {
                      total:
                        naSpaceSelected.spaceTypes.length + naSpaceSelected.customSpaces.length,
                    })}
                    <ArrowForwardIosIcon />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <Button
          data-testid="Step6BackBtn"
          className={styles.back_btn}
          onClick={() => onEdit(paths.steps.customizeBuildOut)}
          size="small"
          variant="contained"
          startIcon={<ArrowBackIosIcon />}>
          {t('common.back')}
        </Button>
        {!isReport && !isExternalAccess && (
          <ShareTeamDrawer
            state={openSharedNeedsAssessmentsDrawer}
            toggleDrawer={setSharedNeedsAssessmentsDrawer}
          />
        )}
        {showCopyDialog && (
          <CopyNeedsAssessment assessment={na} open={showCopyDialog} onClose={onCopyAssessmnent} />
        )}
      </div>
    </>
  );
}
